import { useEffect, useMemo, useRef } from 'react';
import ABCJS from 'abcjs';
import { DRUMS } from '../data/constants';
import { SCORE_ABC, prepareAbcForStaff, type ScoreMeasure } from '../data/scoreData';
import { scrollScoreTargetToTop } from '../utils/drumHelpers';

interface EasyScoreProps {
  measures: ScoreMeasure[];
  current: number;
}

function EasyScore({ measures, current }: EasyScoreProps) {
  const scoreRef = useRef<HTMLDivElement | null>(null);
  const rows = useMemo(() => Array.from({ length: Math.ceil(measures.length / 2) }, (_, index) => measures.slice(index * 2, index * 2 + 2)), [measures]);

  useEffect(() => {
    const activeRow = scoreRef.current?.querySelector(`[data-score-step="${current}"]`)?.closest('.easy-score-row');
    scrollScoreTargetToTop(activeRow);
  }, [current]);

  return (
    <div className="vertical-score easy-score" ref={scoreRef}>
      {rows.map((row, rowIndex) => (
        <div className="easy-score-row" key={rowIndex}>
          <div className="easy-measure-pair">
            {row.map((measure) => (
              <div className="easy-measure" key={measure.index}>
                {Array.from({ length: measure.slots }, (_, position) => {
                  const step = measure.startStep + position;
                  const events = measure.events.filter((event) => event.position === position);
                  const hits = events.filter((event) => !event.rest && event.drumIndex !== 7);
                  const hasKick = events.some((event) => event.drumIndex === 7);
                  return (
                    <span className={`easy-beat ${step === current ? 'is-playing' : ''}`} data-score-step={step} key={position}>
                      {hits.length ? (
                        <span className="easy-hit-stack">
                          {hits.map((event, hitIndex) => (
                            <i className="easy-hit" style={{ '--note': DRUMS[event.drumIndex ?? 0]?.[1] } as React.CSSProperties} key={`${event.drumIndex}-${hitIndex}`} />
                          ))}
                        </span>
                      ) : <i className="easy-rest" />}
                      {hasKick && <i className="easy-kick" />}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface StaffScoreProps {
  difficulty: 'Normal' | 'Hard';
  measures: ScoreMeasure[];
  current: number;
}

function StaffScore({ difficulty, measures, current }: StaffScoreProps) {
  const paperRef = useRef<HTMLDivElement | null>(null);
  const noteStepsByVoice = useMemo(() => {
    const steps: { hands: number[]; feet: number[] } = { hands: [], feet: [] };
    measures.forEach((measure) => {
      (Object.keys(steps) as Array<'hands' | 'feet'>).forEach((voice) => {
        const positions = [...new Set(measure.events
          .filter((event) => !event.rest && event.voice === voice)
          .map((event) => event.position))].sort((a, b) => a - b);
        positions.forEach((position) => steps[voice].push(measure.startStep + position));
      });
    });
    return steps;
  }, [measures]);

  useEffect(() => {
    if (!paperRef.current) return;
    const stageAbc = prepareAbcForStaff(SCORE_ABC[difficulty]).replace(/%%staffsep\s+\d+/, '%%staffsep 90');
    const paperWidth = paperRef.current.clientWidth;
    const scale = Math.min(2, Math.max(1.65, paperWidth / 610));
    ABCJS.renderAbc(paperRef.current, stageAbc, {
      add_classes: true,
      responsive: 'resize',
      staffwidth: Math.max(580, paperWidth / scale - 24),
      scale,
      paddingtop: 8,
      paddingbottom: 8,
      paddingleft: 0,
      paddingright: 0,
      wrap: difficulty === 'Hard' ? {
        preferredMeasuresPerLine: 3,
        minSpacing: 1.8,
        maxSpacing: 2.6,
      } : undefined,
    } as any);
    const voiceOffsets = { hands: 0, feet: 0 };
    [...paperRef.current.querySelectorAll('.abcjs-note')].forEach((note, index) => {
      const voice: 'hands' | 'feet' = note.classList.contains('abcjs-v1') ? 'feet' : 'hands';
      const step = noteStepsByVoice[voice][voiceOffsets[voice]];
      voiceOffsets[voice] += 1;
      (note as HTMLElement).dataset.scoreStep = String(step ?? index);
      (note as HTMLElement).dataset.scoreVoice = voice;
    });
  }, [difficulty, noteStepsByVoice]);

  useEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;
    paper.querySelectorAll('.is-playing').forEach((note) => note.classList.remove('is-playing'));
    const active = [...paper.querySelectorAll(`[data-score-step="${current}"]`)];
    active.forEach((note) => note.classList.add('is-playing'));
    scrollScoreTargetToTop(active[0]?.closest('.abcjs-staff-wrapper'));
  }, [current]);

  return <div className={`vertical-score staff-score ${difficulty === 'Normal' ? 'colorized' : 'classic'}`} ref={paperRef} />;
}

interface GameScoreProps {
  difficulty: 'Easy' | 'Normal' | 'Hard';
  current: number;
  measures: ScoreMeasure[];
}

function GameScore({ difficulty, current, measures }: GameScoreProps) {
  return (
    <div className="score-viewport">
      {difficulty === 'Easy'
        ? <EasyScore measures={measures} current={current} />
        : <StaffScore difficulty={difficulty} measures={measures} current={current} />}
    </div>
  );
}

export default GameScore;
